import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

/**
 * Boutons de la refonte.
 *
 * Quatre intentions, pas une de plus :
 * - `primary` : l'action de l'ecran. Orange, avec sa lueur. Un seul par vue.
 * - `dark`    : l'action secondaire forte (creer un compte, devenir partenaire).
 * - `secondary` : l'action neutre, en contour.
 * - `ghost` / `danger` : le reste.
 *
 * Le desactive n'est pas un bouton translucide : c'est un bouton gris, lisible,
 * qui dit clairement qu'il ne se passera rien.
 */
type Variant = 'primary' | 'dark' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 rounded-xl font-bold transition-colors disabled:cursor-not-allowed';

const variants: Record<Variant, string> = {
  primary:
    'bg-brand-500 text-white shadow-glow hover:bg-brand-600 disabled:bg-ink-100 disabled:text-ink-400 disabled:shadow-none',
  dark: 'bg-ink-950 text-white hover:bg-ink-900 disabled:bg-ink-100 disabled:text-ink-400',
  secondary:
    'border border-ink-300 bg-white font-semibold text-ink-950 hover:border-ink-950 disabled:border-ink-200 disabled:text-ink-400',
  ghost: 'font-semibold text-ink-700 hover:bg-ink-100 disabled:text-ink-400',
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:bg-ink-100 disabled:text-ink-400',
};

const sizes: Record<Size, string> = {
  sm: 'h-10 px-4 text-[13px]',
  md: 'h-11 px-5 text-sm',
  lg: 'h-12 px-6 text-[15px]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', loading = false, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Spinner className="h-4 w-4 border-white/40 border-t-white" />}
      {children}
    </button>
  );
});

export function ButtonLink({
  to,
  className,
  variant = 'primary',
  size = 'md',
  children,
}: {
  to: string;
  className?: string;
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={cn(base, variants[variant], sizes[size], className)}>
      {children}
    </Link>
  );
}
